import { IProductPageStockModel } from "../Product/Interfaces/IProductPageStock";
import { ProductClass } from "../Product/ProductClass";
import { StockLevel } from "../Product/StockLevel";
import { BasketCommon } from "./basketCommon";

declare let ga: Function;
export interface IStyleDetail {
    availability: HashTable<number>;
    brandName: string;
    brandNameImageUrl: string;
    code: string;
    colours: ISelectedColourInfo[];
    description: string;
    formattedPrice: string;
    images: IQuickAddImage[];
    lifestyleShortDescription: string;
    lifestyleLongDescription: string;
    name: string;
    posCodeToIdLookup: HashTable<IProductPageStockModel>;
    pricing: IProductPriceViewModel;
    productClass: ProductClass;
    productLink: string;
    productStyleId: number;
    productStylePosCode: string;
    sizes: HashTable<string>;
    splitColourPricing: boolean;
    styleColourCode: string;
}

export interface IProductPriceViewModel {
    priceWithoutVat: string;
}

export interface ISelectedColourInfo {
    colourId: number;
    colourPosCode: string;
    description: string;
    formattedPrice: string;
}

interface HashTable<T> {
    [key: string]: T;
}

interface IQuickAddImage {
    colour: string;
    colourId?: number;
    imageUrl: string;
}

export class QuickAdd {

    private readonly basketCommon: BasketCommon = new BasketCommon();
    private quickAddElementId = 'QuickAdd_Dialog';
    private quickAddModalElement: HTMLDivElement;
    private currentStyleDetail: IStyleDetail;
    private localeUrlPrefix = document.body.dataset.localeUrlPrefix;

    constructor() {
        if (!document.getElementById(this.quickAddElementId)) {
            return; // Do not initialise if popup for QA is not there.
        }
        this.associateQuickAddLinks();
    }

    private onQuickAddClicked(e: MouseEvent): boolean {
        e.preventDefault();

        let element = e.target as HTMLElement;
        let styleId = parseInt(element.dataset.id);

        this.showQuickAdd(styleId);

        return false;
    }

    private showQuickAdd(styleId: number): void {
        this.quickAddModalElement = this.createQuickAdd(styleId);
        this.requestQuickAddData(styleId);
    }

    public showTheLook(childStyleId: number): void {
        this.quickAddModalElement = this.createQuickAdd(childStyleId);
        this.requestQuickAddData(childStyleId);
    }

    private createQuickAdd(styleId: number): HTMLDivElement {

        if (this.quickAddModalElement) {
            return;
        }

        this.quickAddModalElement = document.getElementById(this.quickAddElementId) as HTMLDivElement;

        this.addListener(".QuickAdd_Sizes", "change", () => this.quickAddSizeChanged());
        this.addListener(".QuickAdd_Colours", "change", () => this.quickAddColourChanged());
        this.addListener("#QuickAddToBasket", "click", () => this.onQuickAddToBasketClicked());

        this.logQuickViewOperation('click');
        this.basketCommon.WireUpPriceEditMonitoring(() => this.onGiftCardPriceChanged());

        return this.quickAddModalElement;
    }

    private addListener(selector: string, eventName: string, callback: () => void) {
        let element = this.quickAddModalElement.querySelector(selector);

        if (element === null) {
            return;
        }

        element.addEventListener(eventName, () => { callback(); });
    }

    private associateQuickAddLinks(): void {
        // Need to use body as the target as the links are dynamically added.
        document.querySelector("body").addEventListener("click", (e) => {
            let element = e.target as HTMLElement;

            if (element.classList.contains("QuickAdd_Show")) {
                this.onQuickAddClicked(e);
            }
        });

    }

    private requestQuickAddData(styleId: number): void {

        fetch(`${this.localeUrlPrefix}/ws/style-info/${styleId}`, { method: "POST" })
            .then(response => {
                if (!response || !response.ok) {
                    throw new Error("Request quick add data failed.");
                }
                return response.json();
            })
            .then((data: IStyleDetail) => {
                this.requestQuickAddDataComplete(data);
            })
            .catch((error: Error) => {
                window.IssueLogger.log(error.message, "QuickAddRequest", error);
            });
    }

    private requestQuickAddDataComplete(data: IStyleDetail): void {
        this.currentStyleDetail = data;
        this.quickAddModalElement.querySelector(".product-name").textContent = data.name;
        this.quickAddModalElement.querySelector(".QuickAdd_ProductLink").setAttribute('href', data.productLink);

        let lifestyle = this.quickAddModalElement.querySelector<HTMLParagraphElement>(".sub-name");

        if (data.lifestyleShortDescription !== "" || data.lifestyleLongDescription !== "") {
            lifestyle.textContent = data.lifestyleShortDescription;
            lifestyle.setAttribute("data-balloon", data.lifestyleLongDescription); // Setting via data won't work, has to be attribute.

            if (data.lifestyleLongDescription !== '') {
                lifestyle.classList.add("data-balloon");
            }
            else {
                lifestyle.classList.remove("data-balloon");
            }
            lifestyle.classList.remove("hidden");
        }
        else {
            lifestyle.classList.add("hidden");
        }

        let supplierImageElement = this.quickAddModalElement.querySelector("#SupplierImage");
        supplierImageElement.setAttribute('src', data.brandNameImageUrl);
        supplierImageElement.setAttribute('alt', data.brandName);

        let productCodeElement = this.quickAddModalElement.querySelector("#product-code");
        productCodeElement.textContent = "Product Code: " + data.code;

        let priceSpanElement = this.quickAddModalElement.querySelector(".item-price");
        let sizeAndColourContainer = this.quickAddModalElement.querySelector("#size-and-colour-container");
        let giftcardContainer = this.quickAddModalElement.querySelector("#giftcard-amount-container");

        if (data.productClass === ProductClass.GiftCard) {
            giftcardContainer.classList.remove("hidden");
            sizeAndColourContainer.classList.add("hidden");
            priceSpanElement.classList.add("hidden");
        }
        else {
            giftcardContainer.classList.add("hidden");
            sizeAndColourContainer.classList.remove("hidden");
            priceSpanElement.classList.remove("hidden");
        }

        this.PopulateThumbnails(data.images);
        this.populateQuickAddSizes(data);

        const bvRatingElement = this.quickAddModalElement.querySelector<HTMLDivElement>(".bazaarvoice");

        if (bvRatingElement) {
            bvRatingElement.dataset.bvProductid = data.styleColourCode;
            bvRatingElement.dataset.bvRedirectUrl = data.productLink;
            bvRatingElement.dataset.bvShow = "inline_rating";
        }

        this.setQuickAddButtonState();

        this.updateSelectedColourDependencies();

        window.Modal.OpenModal(this.quickAddModalElement, () => { });
    }

    private updatePriceWithoutVat(text: string): void {
        let element = this.quickAddModalElement.querySelector(".exvatmessage");

        if (text) {
            let innerPriceElement = this.quickAddModalElement.querySelector("#PriceWithoutVAT");
            innerPriceElement.innerHTML = text;
            element.classList.remove("hidden");
        }
        else {
            element.classList.add("hidden");
        }
    }

    private setElementTextOrHide(element: HTMLSpanElement, text: string): void {

        element.innerHTML = text;

        if (text === '') {
            element.classList.add("hidden");
        }
        else {
            element.classList.remove("hidden");
        }
    }

    private PopulateThumbnails(images: IQuickAddImage[]): void {

        if (MagicScroll.running('quick-view-images') === true) {
            MagicScroll.stop('quick-view-images');
        }

        let container = this.quickAddModalElement.querySelector(".product-list-new");
        container.innerHTML = '';

        for (const image in images) {
            const imageElement = this.CreateScrollImageElement(images[image]);

            container.append(imageElement);
        }
        container.classList.add("MagicScroll");
        MagicScroll.refresh("quick-view-images");
        MagicScroll.start("quick-view-images");
    }

    private CreateScrollImageElement(image: IQuickAddImage): HTMLElement {

        let productImg = document.createElement("img");
        productImg.src = image.imageUrl;
        productImg.alt = image.colour;

        return productImg;
    }

    private populateQuickAddSizes(stock: IStyleDetail): void {
        let selectListElem = this.quickAddModalElement.querySelector<HTMLSelectElement>(".QuickAdd_Sizes");

        // Clear all existing options from the select list.
        selectListElem.options.length = 0;

        // Create and append the default "Select Size" option.
        let defaultOption = document.createElement("option");
        defaultOption.value = "";
        defaultOption.textContent = "Select Size";
        selectListElem.appendChild(defaultOption);

        // Iterate over the sizes and append them as options
        let sizes = stock.sizes;
        for (let sizePosCode in sizes) {
            let option = document.createElement("option");
            option.value = sizePosCode;
            option.textContent = sizes[sizePosCode];
            selectListElem.appendChild(option);
        }

        this.resetQuickAddColours("(Colour) Select Size First");
        this.setQuickAddButtonState();
    }

    private resetQuickAddColours(notSelectedText: string): HTMLSelectElement {
        let selectListElem = this.quickAddModalElement.querySelector<HTMLSelectElement>(".QuickAdd_Colours");

        selectListElem.options.length = 0;
        let option = document.createElement('option');
        option.innerHTML = notSelectedText;
        selectListElem.options.add(option);

        return selectListElem;
    }

    private selectedColourPosCode(): string {
        let colourDropdown = this.quickAddModalElement.querySelector<HTMLSelectElement>(".QuickAdd_Colours");
        let selectedColourPosCode = colourDropdown.selectedOptions.length === 1 ? colourDropdown.selectedOptions[0].value : "";

        return selectedColourPosCode;
    }

    private selectedSizePosCode(): string {
        let sizeDropdown = this.quickAddModalElement.querySelector<HTMLSelectElement>(".QuickAdd_Sizes");
        let selectedSizePosCode = sizeDropdown.selectedOptions.length === 1 ? sizeDropdown.selectedOptions[0].value : "";

        return selectedSizePosCode;
    }

    private populateQuickAddColours(): void {
        let selectList = this.resetQuickAddColours("Select Colour");
        let stock = this.currentStyleDetail;
        let selectedSizePosCode = this.selectedSizePosCode();

        if (selectedSizePosCode === "") {
            // Reset the state of the text stock indicators.
            this.updateSelectedColourDependencies();
            return;
        }

        for (const colour of stock.colours) {
            let posCode = stock.productStylePosCode + colour.colourPosCode + selectedSizePosCode;
            let stockText;
            let cssClass = '';

            switch (stock.availability[posCode]) {
                case StockLevel.Stock_InStock:
                case StockLevel.Stock_LowStock:
                    stockText = 'Order Now';
                    cssClass = 'instock';
                    break;
                case StockLevel.Stock_Enquiries:
                    stockText = 'Out of Stock (Request Email Alert)';
                    cssClass = 'requestemail';
                    break;
                default:
                    stockText = 'Discontinued and Sold Out';
                    cssClass = 'discontinued';
            }

            let colourOptionText = colour.description + " - " + stockText;

            if (stock.splitColourPricing) {
                if (colour.formattedPrice !== null && colour.formattedPrice !== '') {
                    colourOptionText += " (" + colour.formattedPrice + ")";
                }
                else {
                    colourOptionText += " (" + stock.formattedPrice + ")";
                }
            }

            let option = document.createElement('option');
            option.className = cssClass;
            option.value = colour.colourPosCode;
            option.innerHTML = colourOptionText;
            selectList.options.add(option);
        }

        let colourCount = selectList.options.length;
        if (colourCount == 2) {
            selectList.selectedIndex = 1;
            selectList.dispatchEvent(new Event("change"));
        }

        this.setQuickAddButtonState();
    }

    private quickAddSizeChanged(): void {
        this.populateQuickAddColours();
    }

    private onGiftCardPriceChanged(): void {
        this.setQuickAddButtonState();
    }

    private onQuickAddToBasketClicked(): void {
        let stockInfo = this.getQuickAddSelectedStockInfo();

        this.logQuickViewOperation('Add To Basket');
        switch (stockInfo.availability) {
            case StockLevel.Stock_InStock:
            case StockLevel.Stock_LowStock:
                let price: number = null;

                if (stockInfo.productClass === ProductClass.GiftCard) {
                    price = this.basketCommon.giftCardValue();

                    if (price === null) {
                        alert('Please enter a gift card value of at least £5.');
                        return;
                    }
                }
                else {
                    price = stockInfo.priceGbp;
                }
                this.basketCommon.AddToBasket(stockInfo.productId, price);
                break;
            case StockLevel.Stock_Enquiries:
            case StockLevel.Stock_Unavailable:
                window.location.href = "/out-of-stock?ProductId=" + stockInfo.productId;
                break;
            default:
                alert('You need to select a size and colour combination first.');
        }
    }

    private logQuickViewOperation(eventAction): void {
        if (typeof ga != "undefined") {
            ga('send', 'event', 'QuickView', eventAction);
        }
    }

    public addToWishList(productId: number, clickedButton: HTMLButtonElement): void {

        if (clickedButton.dataset.fetching) {
            // Not returned from a previous add to wishlist, ignore this click for now.
            return;
        }
        clickedButton.dataset.fetching = "true";

        let postData = { productId: productId };

        fetch("/add-to-wishlist",
            {
                body: JSON.stringify(postData),
                headers: { "Content-Type": "application/json" },
                method: "POST",
            })
            .then(response => {
                // Remove flag that prevents double posting.
                delete clickedButton.dataset.fetching;

                if (!response || !response.ok) {
                    if (response.status === 401) {
                        this.showWishlistLoginRequired();
                        return;
                    }
                    else {
                        throw new Error("Add to wishlist failed.");
                    }
                }
                this.showAddToWishListSuccess();
            })
            .catch((error: Error) => {
                window.IssueLogger.log(error.message, "addToWishList", error);
                this.showAddToWishListFailed();

                // Remove flag that prevents double posting.
                delete clickedButton.dataset.fetching;
            });
    }

    private showAddToWishListSuccess(): void {
        let dialog = document.getElementById("wishlist-success") as HTMLDivElement;

        window.Modal.OpenModal(dialog, () => { });
    }

    private showWishlistLoginRequired(): void {
        let dialog = document.getElementById("wishlist-login") as HTMLDivElement;

        window.Modal.OpenModal(dialog, () => { });
    }

    private showAddToWishListFailed(): void {
        let dialog = document.getElementById("wishlist-error-modal") as HTMLDivElement;

        window.Modal.OpenModal(dialog, () => { });
    }

    private quickAddColourChanged(): void {
        this.updateSelectedColourDependencies()
    }

    private updateSelectedColourDependencies(): void {
        let data = this.currentStyleDetail;

        this.setQuickAddButtonState();

        let stockInfo = this.getQuickAddSelectedStockInfo();
        let selectedColour = this.getQuickAddSelectedColour();

        // Use style price by default unless colour is selected and the colour has a price.
        let exVatPrice = data.pricing.priceWithoutVat;
        let price = data.formattedPrice;
        if (selectedColour && stockInfo.price) {
            exVatPrice = stockInfo.priceExVat;
            price = stockInfo.price;

        }
        this.updatePriceWithoutVat(exVatPrice);
        this.quickAddModalElement.querySelector('.item-price').innerHTML = price;

        let stockMessageElement = this.quickAddModalElement.querySelector<HTMLSpanElement>(".stock-message");
        let zoomCaptionElement = this.quickAddModalElement.querySelector<HTMLHeadingElement>("#quickViewZoomCaption");

        if (selectedColour === null) {
            stockMessageElement.textContent = "Select size and colour above to view live stock availability";
            zoomCaptionElement.classList.add("hidden");
        }
        else if (stockInfo.stockLevelMessage) {
            stockMessageElement.textContent = stockInfo.stockLevelMessage;
            zoomCaptionElement.textContent = selectedColour.description;
            zoomCaptionElement.classList.remove("hidden");
        } else {
            stockMessageElement.textContent = "Discontinued and Sold Out";
            zoomCaptionElement.textContent = selectedColour.description;
            zoomCaptionElement.classList.remove("hidden");
        }

        let percentageOffElement = this.quickAddModalElement.querySelector<HTMLSpanElement>('.percentage-off');
        this.setElementTextOrHide(percentageOffElement, stockInfo.percentageSaving);
    }

    private getQuickAddSelectedStockInfo(): IProductPageStockModel {

        if (this.currentStyleDetail.productClass === ProductClass.GiftCard) {
            if (this.basketCommon.giftCardValue() === null) {
                return this.NotAvailable(ProductClass.GiftCard);
            }

            let firstProductPosCode = Object.keys(this.currentStyleDetail.posCodeToIdLookup)[0];
            return this.CreateStockInfo(ProductClass.GiftCard, this.currentStyleDetail.posCodeToIdLookup[firstProductPosCode]);
        }

        let selectedSizePosCode = this.selectedSizePosCode();
        let selectedColourPosCode = this.selectedColourPosCode();
        let posCode = this.currentStyleDetail.productStylePosCode + selectedColourPosCode + selectedSizePosCode;

        let stockInfo = this.currentStyleDetail.posCodeToIdLookup[posCode];

        if (stockInfo) {
            return this.CreateStockInfo(ProductClass.PhysicalProduct, stockInfo);
        }

        return this.NotAvailable(ProductClass.PhysicalProduct);
    }

    private CreateStockInfo(productClass: ProductClass, stockData: IProductPageStockModel): IProductPageStockModel {
        stockData["ProductClass"] = productClass;
        return stockData;
    }

    private NotAvailable(productClass: ProductClass): IProductPageStockModel {
        return {
            availability: StockLevel.Stock_NotSelected,
            percentageSaving: '',
            posCode: '',
            priceHeading: '',
            price: "",
            priceExVat: "",
            priceGbp: 0,
            productClass: productClass,
            productId: 0,
            stockLevelMessage: '',
        };
    }

    private getQuickAddSelectedColour(): ISelectedColourInfo {
        let stock = this.currentStyleDetail;
        let selectedColourPos = this.selectedColourPosCode();

        for (let i in stock.colours) {
            let col = stock.colours[i];

            if (col.colourPosCode == selectedColourPos) {
                return col;
            }
        }

        return null;
    }

    private setQuickAddButtonState(): void {
        let buttonClass;
        let buttonText;
        let buttonDisabled = false;
        let buttonRef = this.quickAddModalElement.querySelector<HTMLButtonElement>("#QuickAddToBasket");
        let stockInfo = this.getQuickAddSelectedStockInfo();

        switch (stockInfo.availability) {
            case StockLevel.Stock_InStock:
            case StockLevel.Stock_LowStock:
                buttonClass = "btn btn-primary on";
                buttonText = "Add to Basket";
                break;
            case StockLevel.Stock_Enquiries:
            case StockLevel.Stock_Unavailable:
                buttonClass = "btn btn-primary outofstock notice";
                buttonText = "Email me when in stock";
                break;
            default:
                buttonClass = "btn btn-primary off";
                buttonText = 'Add to Basket';
                buttonDisabled = true;
        }

        // Find the last element in the button which is a text node.
        let buttonTextNode = buttonRef.childNodes[buttonRef.childNodes.length - 1];
        buttonTextNode.nodeValue = buttonText;

        buttonRef.setAttribute('class', buttonClass);
        buttonRef.disabled = buttonDisabled;
    }
}
